import type { ChangelogEntry } from "../types";

/**
 * UX-bølgen fra VIB-2176 (13 PR-er, #940 til #952). Teksten er den samme som
 * i «Nytt i Revehiet»-e-posten 25.09.2026, med rolle- og flagg-gating der
 * funksjonen ikke finnes for alle.
 */
export const UX_BOLGEN_2026_09: ChangelogEntry = {
  id: "2026-09-29-ux-bolgen",
  date: "2026-09-29",
  title: "Tastatur og tilgjengelighet i hele Revehiet",
  summary:
    "Vi har gått gjennom hele Revehiet med tastaturet og rettet det som stoppet. Alt du gjorde med mus virker som før, men nå kan du også la musa ligge.",
  sections: [
    {
      heading: "Overalt i Revehiet",
      items: [
        {
          title: "Tab én gang etter innlasting viser «Hopp til innhold»",
          kind: "nytt",
          body: "Enter hopper rett forbi venstremenyen og inn i siden. Før måtte du tabbe gjennom alle menypunktene hver gang.",
        },
        {
          title: "Tabellrader og kolonneoverskrifter kan nås med Tab",
          kind: "nytt",
          body: "Prosjekter, Tilbud, Ansatte, Rapporter, Mine oppgaver og Måltall-widgeten. Enter åpner raden, Enter på en overskrift sorterer, og pilen viser faktisk retning.",
        },
        {
          title: "Knapper som før bare dukket opp når du holdt musa over, vises nå også ved tastaturfokus",
          kind: "forbedret",
          body: "Rediger tildeling, slett fravær, send og slett statist, slett tilbudslinje og lignende. Slett-knappen på tilbudslinjer kan nå nås med Tab.",
        },
        {
          title: "Tydelig fokusring i venstremenyen og mobilmenyen",
          kind: "forbedret",
          body: "Skjermleser sier også hvilken side som er aktiv.",
        },
        {
          title: "Fokus kommer tilbake dit du var etter ⌘ K og Feedback",
          kind: "forbedret",
          body: "Esc lukker, og Tab fortsetter der du slapp, ikke fra toppen av siden.",
        },
        {
          title: "404-siden er på norsk",
          kind: "rettet",
          body: "«Gå til forsiden» bytter side uten hvit blink.",
        },
      ],
    },
    {
      heading: "Prosjektlisten og Nytt prosjekt",
      minRole: "user",
      items: [
        {
          title: "↑ ↓ i prosjektlisten følger den sorteringen du ser",
          kind: "forbedret",
          body: "Markert rad scroller inn i bildet, og Enter åpner riktig prosjekt. Interne prosjekter sorteres nå likt med kundeprosjekter.",
        },
        {
          title: "Nytt prosjekt starter med markøren i prosjektnavn",
          kind: "forbedret",
          body: "Trykker du Neste uten å fylle ut, hopper markøren til feltet som mangler, i tillegg til feilmeldingen.",
        },
        {
          title: "Stegindikatoren øverst i Nytt prosjekt hopper over steg du ikke har nådd",
          kind: "forbedret",
          body: "Aktivt steg har synlig fokusring, og «+»-knappen ved kundevelgeren heter «Ny kunde» for skjermleser.",
        },
      ],
    },
    {
      heading: "Inne på et prosjekt",
      minRole: "user",
      items: [
        {
          title: "Sjekklistene i Oppstartsmøte, Statusmøte, Debrief og Lukk prosjekt kan krysses av med Space",
          kind: "nytt",
          body: "Tab lander på punktene, ikke forbi dem. Lukk prosjekt starter med fokus på «GEEK-kalkulering godkjent» i stedet for på «Gå til faktura».",
        },
        {
          title: "← → på fanelinjen bytter fane",
          kind: "nytt",
          body: "Home og End går til første og siste fane.",
        },
        {
          title: "Budsjett: Enter i et timefelt lagrer tildelingen, Esc lukker uten å lagre",
          kind: "nytt",
          body: "Enter har samme sperre som Lagre-knappen, så du kan ikke lagre over budsjett.",
        },
        {
          title: "Statister: Esc angrer det du skrev i feltet",
          kind: "nytt",
          body: "Rolle, adresse, personnummer, kontonummer, e-post og telefon. Feltene kan også åpnes for redigering med tastaturet.",
        },
        {
          title: "Prosjektoppdateringer: @ og piltaster velger person",
          kind: "nytt",
          body: "Enter eller Tab setter inn valgt person, Esc lukker listen. Meldingen sendes ikke før du trykker ⌘ Enter.",
        },
      ],
    },
    {
      heading: "Mine oppgaver og Logg tid",
      minRole: "user",
      items: [
        {
          title: "Enter i kommentarfeltet gir linjeskift, ⌘ Enter lagrer",
          kind: "forbedret",
          body: "Enter i Fra og Til lagrer som før. Samme konvensjon som i prosjektoppdateringer.",
        },
        {
          title: "Tab fra Fra til Til virker",
          kind: "rettet",
          body: "Skriv «9», Tab, «11»: Fra blir 09:00 og Til 11:00. Før hoppet markøren tilbake til Fra.",
        },
        {
          title: "Tom oppgaveliste sier ifra",
          kind: "forbedret",
          body: "«Ingen tildelte oppgaver akkurat nå» i stedet for en tom flate.",
        },
        {
          title: "Meld fravær fra Avslutt måned setter fokus i skjemaet med en gang",
          kind: "forbedret",
          body: "Stopp timer, Godkjenn, Avslå og Trekk søknad i timebank har fått navn som skjermleser leser opp.",
        },
      ],
    },
    {
      heading: "Tilbud",
      minRole: "user",
      items: [
        {
          title: "⌥ ↑ ⌥ ↓ flytter linjen du står i ett hakk opp eller ned",
          kind: "nytt",
          body: "Fokus følger linjen. Står også i snarveilisten under ?.",
        },
        {
          title: "Enter i tittelfeltet oppretter nytt tilbud",
          kind: "nytt",
          body: "Når tittel, kunde og vilkår er fylt ut. Mangler noe, vises valideringen i stedet.",
        },
        {
          title: "Pipeline-brettet kan betjenes med tastaturet",
          kind: "nytt",
          body: "Tab til et kort, mellomrom, piler, mellomrom for å flytte det. Hintet står ved filtrene, og kortene har synlig fokusring.",
          featureFlag: "feature_offer_pipeline",
        },
      ],
    },
    {
      heading: "Faktura, kunder og avtaler",
      minRole: "user",
      items: [
        {
          title: "Faktura har fått søkefelt, / hopper dit",
          kind: "nytt",
          body: "Søk på kunde og prosjektnummer. Beløp og Dato kan sorteres, første klikk gir størst eller nyest først.",
        },
        {
          title: "Prosjektnummeret i fakturalisten er en lenke til prosjektet",
          kind: "nytt",
          body: "Siden viser spinner mens den laster, og skiller «Ingen fakturaer ennå» fra «Ingen fakturaer matcher filteret».",
        },
        {
          title: "Enter i Ny avtale lagrer avtalen",
          kind: "nytt",
          body: "Fra et hvilket som helst tekstfelt, når skjemaet er gyldig.",
        },
        {
          title: "Godkjenn bilag setter fokus i panelet, og feltene har fått navn",
          kind: "forbedret",
          body: "Skjermleser sier «Knytt til budsjettpost», «Avtalt pris» og «Kommentar til daglig leder». PDF-knappen i prosjektets fakturaliste sier hvilken faktura den åpner.",
        },
      ],
    },
    {
      heading: "Innstillinger og Hjelp",
      items: [
        {
          title: "Angre-knapp på Systemroller, Prosjektroller og Funksjonsbrytere",
          kind: "nytt",
          body: "Hver endring gir en toast med «Angre» i 8 sekunder. Før skrev ett klikk rett til databasen.",
          minRole: "admin",
        },
        {
          title: "Rollebytte på en bruker må bekreftes",
          kind: "nytt",
          body: "Avbryt lar rollen stå som den var.",
          minRole: "admin",
        },
        {
          title: "Piltaster i tilgangsmatrisene",
          kind: "nytt",
          body: "Tab lander på én celle, piler flytter, Home og End går til kanten, Space krysser av. Piler på fanelinjen (Systemroller, Prosjektroller, …) bytter fane.",
          minRole: "admin",
        },
        {
          title: "Produktkatalog: Enter lagrer raden, Esc forkaster",
          kind: "nytt",
          body: "«Tilbake til innstillinger» bytter side uten hvit blink.",
          minRole: "admin",
        },
        {
          title: "Hjelp-søket: ↑ ↓ velger treff, Enter åpner",
          kind: "nytt",
          body: "Esc tømmer søket.",
        },
      ],
    },
    {
      heading: "Rettinger",
      items: [
        {
          title: "Enter i kommentaren lagret timene",
          kind: "rettet",
          body: "I Logg tid lagret Enter registreringen uansett hvor markøren sto, også midt i en kommentar. Nå gir Enter linjeskift i kommentarfeltet, og ⌘ Enter lagrer.",
        },
        {
          title: "Lukk prosjekt hoppet til Faktura ved Enter",
          kind: "rettet",
          body: "Dialogen ga fokus til «Gå til faktura», så en Enter-refleks rett etter åpning sendte deg ut av lukkeflyten. Fokus starter nå på første sjekklistepunkt.",
        },
        {
          title: "Prosjekt kunne ikke lukkes uten mus",
          kind: "rettet",
          body: "Sjekklistepunktene lå utenfor Tab-rekkefølgen, og siden «GEEK-kalkulering godkjent» er påkrevd, satt tastaturbrukere fast. Nå er punktene vanlige avkrysningsbokser.",
        },
        {
          title: "Piltastene i prosjektlisten traff feil prosjekt",
          kind: "rettet",
          body: "Markeringen fulgte den usorterte lista, ikke den du så på skjermen, og raden scrollet ikke inn. Begge er rettet.",
        },
        {
          title: "Ekstern-rollen landet på 404 etter innlogging",
          kind: "rettet",
          body: "Forsiden pekte på Måltall eller Mine oppgaver, som eksterne ikke har tilgang til. Nå lander du på første side rollen din har tilgang til.",
        },
      ],
    },
  ],
};
